/**
 * Silent session refresh, single-flight.
 *
 * Access tokens are short-lived. Without this, a user is signed out mid-task —
 * a cashier loses a half-typed payment, a teacher loses a roll-call — and the
 * only symptom is "l'application m'a déconnecté". The React front shipped
 * without it.
 *
 * SINGLE-FLIGHT IS NOT AN OPTIMISATION, IT IS THE CONTRACT. The backend issues
 * ROTATE-ON-USE refresh tokens: every call to POST /api/v1/auth/refresh
 * deny-lists the previous JTI *before* minting the new pair. So if a screen
 * fires five requests, all five 401, and each starts its own refresh, the first
 * wins and the other four come back `refresh_token_replayed` — which reads as
 * a stolen token and kills the session. Every concurrent caller therefore
 * shares ONE in-flight promise. A dashboard opening four tiles at once is the
 * normal case here, not a corner case.
 *
 * Two more things this must get right:
 *
 *  - The refresh cookie is PATH-SCOPED to /api/v1/auth/refresh, so the browser
 *    only sends it to that exact route. `credentials: "include"` carries it.
 *  - The refresh route is NOT CSRF-exempt. Under an enforced deployment a
 *    header-less refresh 403s, and the user looks signed out on a cold boot.
 *
 * This module does its own fetch and never goes through request(), so it
 * cannot recurse into the 401 handler that calls it.
 */
import { ensureCsrfToken, readCsrfCookie } from "./csrf";

export const REFRESH_PATH = "/api/v1/auth/refresh";

let inflight: Promise<boolean> | null = null;

/**
 * Try once to mint a fresh session. Concurrent callers share the attempt.
 *
 * Resolves true when a new session exists, false otherwise (no refresh cookie,
 * expired, replayed, or offline). Never throws: the caller's next move is to
 * end the session, and an exception here would just be caught and discarded.
 */
export function attemptRefresh(setBearer: (token: string) => void): Promise<boolean> {
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      // Seed BEFORE the POST. On a cold boot — a returning user whose access
      // token expired overnight — no CSRF cookie exists yet, and without this
      // an enforced deployment 403s the very request that would have restored
      // their session.
      await ensureCsrfToken();
      const headers: Record<string, string> = {};
      const csrf = readCsrfCookie();
      if (csrf) headers["X-CSRF-Token"] = csrf;

      const response = await fetch(REFRESH_PATH, {
        method: "POST",
        headers,
        credentials: "include",
      });
      if (!response.ok) return false;

      // On the cookie rail the authoritative session is the freshly re-set
      // HttpOnly cookie and the body's access_token is noise. On the Bearer
      // rail (Capacitor) it is the whole point, so it is kept in memory.
      const body = await response.json().catch(() => null);
      const token = (body as { access_token?: string } | null)?.access_token;
      if (token) setBearer(token);
      return true;
    } catch {
      return false;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/** Test seam: is a refresh in flight right now? */
export const forTest = { isInflight: () => inflight !== null };
